import React, { useState } from "react";
import { toast } from "sonner";
import { trackFunnelEvent } from "../../utils/analytics";
import { 
  Bus, Truck, Package, Car, ChevronRight, ArrowLeft, Leaf,
  Building2, User, Mail, Phone, ClipboardList, Rocket, Zap
} from "lucide-react";

const ClientEntry = ({ config, onContinue, onBack, onTalkExpert }) => {
  const [step, setStep] = useState(1); // 1: Sector, 2: Datos contacto
  const [formData, setFormData] = useState({
    empresa: "", contacto: "", email: "", telefono: "",
    sector: "urbano", comercialId: "",
    // IMPL-041 (2026-05-18) · tipoAnalisis fijado a 'quick' (flujo guiado). El usuario ya no elige.
    tipoAnalisis: "quick"
  });

  const sectorIcons = {
    urbano: <Bus className="w-6 h-6" />, interurbano: <Bus className="w-6 h-6" />,
    distribucion: <Package className="w-6 h-6" />, logistica: <Truck className="w-6 h-6" />, taxi: <Car className="w-6 h-6" />
  };

  const handleNextStep = () => {
    // IMPL-055 · sector confirmado al pasar al siguiente paso
    trackFunnelEvent("entry", "sector_seleccionado", { sector: formData.sector });
    setStep(2);
    window.scrollTo(0, 0);
  };

  const handleContinue = () => {
    if (!formData.empresa.trim()) { toast.error("Introduce el nombre de la empresa"); return; }
    if (!formData.contacto.trim()) { toast.error("Introduce el nombre del contacto"); return; }
    if (!formData.telefono.trim()) { toast.error("Introduce el teléfono de contacto"); return; }
    // IMPL-055 · datos cliente completados, entra al Simulator
    trackFunnelEvent("entry", "flota_completada", { sector: formData.sector });
    onContinue(formData);
  };

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="px-6 py-6 border-b border-slate-200">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <button onClick={step === 1 ? onBack : () => setStep(1)} className="p-2 hover:bg-slate-100 rounded-lg transition-all text-slate-500" data-testid="back-to-landing-btn"><ArrowLeft className="w-5 h-5" /></button>
          <img
            src="/logo-mobilsafe.png"
            alt="MOBILSAFE"
            className="h-10 md:h-12"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
          <div className="hidden items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center shadow-lg"><Leaf className="w-6 h-6 text-white" /></div>
            <div><h1 className="text-xl font-black text-slate-900">MOBILSAFE</h1><p className="text-[10px] text-emerald-600 tracking-widest font-semibold">FLEET TECHNOLOGY</p></div>
          </div>
          <div className="border-l border-slate-200 pl-3 ml-1">
            <p className="text-xs text-emerald-600 font-semibold tracking-wide uppercase">Tu Análisis de Flota</p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* ==================== PASO 1: Sector y Tipo de Análisis ==================== */}
        {step === 1 && (
          <>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-emerald-100 border border-emerald-300 px-4 py-2 rounded-full mb-6">
                <Truck className="w-4 h-4 text-emerald-600" /><span className="text-emerald-700 text-sm font-medium">Paso 1 de 3</span>
              </div>
              <h2 className="text-4xl font-black mb-4 text-slate-900">Configura tu Análisis</h2>
              <p className="text-slate-500 text-lg">Selecciona el sector de la flota y el tipo de análisis</p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 space-y-6">
              {/* Sector */}
              <div>
                <label className="block text-sm font-bold mb-3 flex items-center gap-2 text-slate-700"><Truck className="w-4 h-4 text-emerald-600" /> ¿En qué sector opera la flota? *</label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {config.benchmarks && Object.entries(config.benchmarks).map(([key, bench]) => (
                    <button key={key} onClick={() => setFormData({...formData, sector: key})} className={`p-4 rounded-xl border-2 transition-all text-center ${formData.sector === key ? 'bg-emerald-100 border-emerald-500 ring-2 ring-emerald-500/30' : 'bg-white border-slate-200 hover:border-emerald-300 hover:bg-emerald-50'}`} data-testid={`sector-btn-${key}`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${formData.sector === key ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-600'}`}>{sectorIcons[key]}</div>
                      <p className="text-xl mb-1">{bench.icon}</p><p className="font-bold text-sm text-slate-700">{bench.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* IMPL-041 (2026-05-18) · sección "Tipo de Análisis" eliminada · tipoAnalisis queda fijo en 'quick'. */}

              <button onClick={handleNextStep} className="w-full px-8 py-5 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white rounded-xl font-bold text-xl shadow-xl transition-all transform hover:scale-[1.02] flex items-center justify-center gap-3" data-testid="next-step-btn">
                Siguiente <ChevronRight className="w-6 h-6" />
              </button>

              {/* IMPL-041 · CTA secundario · agenda directa con un experto sin pasar por CAES landing */}
              {onTalkExpert && (
                <button
                  onClick={() => onTalkExpert(formData)}
                  data-testid="talk-expert-btn"
                  className="w-full mt-2 px-4 py-2 text-sm font-semibold hover:underline transition"
                  style={{ color: "#ec561c" }}
                >
                  Prefiero hablar con un experto →
                </button>
              )}
            </div>
          </>
        )}

        {/* ==================== PASO 2: Datos de Contacto ==================== */}
        {step === 2 && (
          <>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-emerald-100 border border-emerald-300 px-4 py-2 rounded-full mb-6">
                <User className="w-4 h-4 text-emerald-600" /><span className="text-emerald-700 text-sm font-medium">Paso 2 de 3</span>
              </div>
              <h2 className="text-4xl font-black mb-4 text-slate-900">Datos del Cliente</h2>
              <p className="text-slate-500 text-lg">Personalizaremos el análisis para tu cliente</p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold mb-2 flex items-center gap-2 text-slate-700"><Building2 className="w-4 h-4 text-emerald-600" /> Nombre de la Empresa *</label>
                  <input type="text" value={formData.empresa} onChange={(e) => setFormData({...formData, empresa: e.target.value})} placeholder="Ej: Transportes García S.L." className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all placeholder-slate-400 text-slate-800" data-testid="input-empresa" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2 flex items-center gap-2 text-slate-700"><User className="w-4 h-4 text-emerald-600" /> Nombre del Contacto *</label>
                  <input type="text" value={formData.contacto} onChange={(e) => setFormData({...formData, contacto: e.target.value})} placeholder="Ej: Juan García" className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all placeholder-slate-400 text-slate-800" data-testid="input-contacto" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2 flex items-center gap-2 text-slate-700"><Mail className="w-4 h-4 text-emerald-600" /> Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Ej: jgarcia@empresa.es" className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all placeholder-slate-400 text-slate-800" data-testid="input-email" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2 flex items-center gap-2 text-slate-700"><Phone className="w-4 h-4 text-emerald-600" /> Teléfono *</label>
                  <input type="tel" value={formData.telefono} onChange={(e) => setFormData({...formData, telefono: e.target.value})} placeholder="Ej: +34 600 000 000" className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all placeholder-slate-400 text-slate-800" data-testid="input-telefono" required />
                </div>
              </div>

              {/* Resumen de selección */}
              <div className="pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-2">Configuración seleccionada:</p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-3 py-1 bg-emerald-100 border border-emerald-300 rounded-full text-sm text-slate-700">
                    Sector: <strong className="text-emerald-700">{config.benchmarks?.[formData.sector]?.name || formData.sector}</strong>
                  </span>
                  <span className="px-3 py-1 bg-emerald-100 border border-emerald-300 rounded-full text-sm text-slate-700">
                    Análisis: <strong className="text-emerald-700">{formData.tipoAnalisis === 'quick' ? 'Descubre tu potencial' : 'Calcula tu ahorro'}</strong>
                  </span>
                </div>
              </div>

              <button onClick={handleContinue} className="w-full px-8 py-5 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white rounded-xl font-bold text-xl shadow-xl transition-all transform hover:scale-[1.02] flex items-center justify-center gap-3" data-testid="continue-btn">
                Continuar con el análisis <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Footer con enlaces legales */}
      <footer className="py-6 border-t border-slate-200 mt-auto">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex flex-wrap justify-center gap-4 mb-2 text-sm">
            <a 
              href="https://www.mobilsafe.es/condiciones-de-uso-y-privacidad/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-emerald-600 transition-all"
            >
              Condiciones de uso y privacidad
            </a>
            <span className="text-slate-300">|</span>
            <a 
              href="https://www.mobilsafe.es/politica-de-cookies/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-emerald-600 transition-all"
            >
              Política de cookies
            </a>
          </div>
          <p className="text-slate-400 text-xs">© 2026 MOBILSAFE Fleet Technology</p>
        </div>
      </footer>
    </div>
  );
};

export default ClientEntry;
